import { useState } from "react";
import { Button, Panel, RangeSlider, Typography } from "@/components/atoms";
import { clsx } from "@/utils";
import { CalculateMyPriceModal } from "./CalculateMyPriceModal";

const MIN_BOOKINGS = 0;
const MAX_BOOKINGS = 50000;

const formatBookingValue = (value: number) => {
  if (value >= 50000) return "50,000+";
  return value.toLocaleString();
};

export const AverageMonthlyBookingPanel = () => {
  const [monthlyBookings, setMonthlyBookings] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCalculatePrice = () => {
    setIsModalOpen(true);
  };

  const getTooltipStyle = () => {
    const percentage = ((monthlyBookings - MIN_BOOKINGS) / (MAX_BOOKINGS - MIN_BOOKINGS)) * 100;

    if (percentage <= 15) {
      // Bottom-right of thumb
      return {
        left: `calc(${percentage}% + 12px)`,
        transform: "translateX(0)",
      };
    } else if (percentage >= 85) {
      // Bottom-left of thumb
      return {
        left: `calc(${percentage}% - 12px)`,
        transform: "translateX(-100%)",
      };
    } else {
      // Centered below thumb
      return {
        left: `${percentage}%`,
        transform: "translateX(-50%)",
      };
    }
  };

  return (
    <>
      <Panel className="flex flex-col gap-6">
        {/* Header with Calculate Button */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-1">
            <Typography variant="action">Average Monthly Booking</Typography>
            <Typography variant="paragraph" className="text-neutral-dark-gray">
              A guide to help you estimate your plan needs based on monthly activity
            </Typography>
          </div>
          <Button variant="primary" size="md" className="w-full sm:w-auto" onClick={handleCalculatePrice}>
            Calculate My Price
          </Button>
        </div>

        {/* Slider section */}
        <div className="flex flex-col gap-4">
          {/* Min and Max labels */}
          <div className="flex flex-row justify-between text-neutral-dark-gray">
            <Typography variant="paragraph">{MIN_BOOKINGS}</Typography>
            <Typography variant="paragraph">{formatBookingValue(MAX_BOOKINGS)}</Typography>
          </div>

          {/* Slider with floating tooltip */}
          <div className="relative">
            <RangeSlider
              min={MIN_BOOKINGS}
              max={MAX_BOOKINGS}
              value={monthlyBookings}
              onChange={(value) => setMonthlyBookings(value as number)}
            />

            {/* Floating tooltip that follows the slider thumb */}
            <div
              className={clsx(
                "absolute top-5 rounded-lg border border-neutral-mid-gray bg-white px-3 py-1.5 shadow-sm",
                "pointer-events-none z-10 whitespace-nowrap"
              )}
              style={getTooltipStyle()}
            >
              <Typography variant="action" className="text-neutral-dark-gray">
                {formatBookingValue(monthlyBookings)} Monthly Bookings
              </Typography>
            </div>
          </div>
        </div>
      </Panel>

      {/* Calculate My Price Modal */}
      <CalculateMyPriceModal open={isModalOpen} onClose={() => setIsModalOpen(false)} initialMonthlyBookings={monthlyBookings} />
    </>
  );
};
